import { isArray } from "lodash-es";
import React, { memo, useCallback, useEffect, useMemo } from "react";

import { ICatalogue } from "../../../apis/useGetCatalogueData/types";
import Condition from "../../../components/Conditional/Condition";
import { useConfigContext } from "../../../contexts/ConfigContext/ConfigContext";
import { IBannerItem } from "../../../contexts/ConfigContext/types";
import { PAGE_PARAM } from "../../constants";
import Banner from "../Banner/Banner";
import CategoryList from "../CategoryList/CategoryList";
import ProductSection from "../ProductSection/ProductSection";
import { useHeightObserver } from "../hooks/useHeightObserver";
import { useIntersectionObserver } from "../hooks/useIntersectionObserver";
import { ICategoryList } from "../types";
import "./ProductList.scss";
import "./ProductList_kokoro.scss";
import "./ProductList_shabuDays.scss";
import "./ProductList_torikizoku.scss";

const VIRTUAL_CATEGORY_ID_PREFIX = "vc:";

interface IProductListProps {
	categories: ICategoryList[];
	products: ICatalogue[];
	onScrollUpdateCategory: (categoryId: string) => void;
	listRef: React.RefObject<HTMLDivElement>;
	sectionRefs: React.MutableRefObject<Map<string, HTMLDivElement | null>>;
	isClickScrolling: React.RefObject<boolean>;
	bannerImages: IBannerItem[];
	bannerClassName: string;
	activeCategoryId: string | null;
	onCategoryClick: (categoryId: string) => void;
	onItemClick: (brand_menu_item_id: number, mode?: PAGE_PARAM, cart_item_index?: string) => void;
	categoryListHeightRef?: React.MutableRefObject<number>;
}

interface IProductCategoryWithProducts extends ICategoryList {
	products: ICatalogue[];
}

const ProductList: React.FC<IProductListProps> = ({
	categories,
	products,
	onScrollUpdateCategory,
	listRef,
	sectionRefs,
	isClickScrolling,
	bannerImages,
	bannerClassName,
	activeCategoryId,
	onCategoryClick,
	onItemClick,
	categoryListHeightRef,
}) => {
	// Use custom hook for height observation
	const { bannerHeight, categoryListHeight } = useHeightObserver(listRef);

	useEffect(() => {
		if (categoryListHeightRef) categoryListHeightRef.current = categoryListHeight;
	}, [categoryListHeight, categoryListHeightRef]);
	const {
		state: { VDPfooter, showBanner },
	} = useConfigContext((state) => state.config);
	const productsByCategory = useMemo(() => {
		// Real category bucketing — keyed by stringified brand_category_id.
		const realProductsByCategoryId = products.reduce<Record<string, ICatalogue[]>>(
			(acc, product) => {
				const categoryId = product?.food_supplier_category?.brand_category_id?.toString();

				if (categoryId) {
					acc[categoryId] = acc[categoryId] || [];
					acc[categoryId].push(product);
				}

				return acc;
			},
			{}
		);

		return categories.reduce<IProductCategoryWithProducts[]>((acc, category) => {
			let productsForCategory: ICatalogue[] | undefined;

			if (category.kind === "virtual") {
				// Virtual category id is namespaced as `vc:<tag_key>` upstream; strip
				// the prefix to recover the raw tag_key for tag matching. A product
				// may appear simultaneously in its real category section AND in one
				// or more virtual sections — its business identity (product.id and
				// brand_menu_item_id) is unchanged; only the React per-section key is
				// composed differently in ProductSection.
				const tagKey = category.id.startsWith(VIRTUAL_CATEGORY_ID_PREFIX)
					? category.id.slice(VIRTUAL_CATEGORY_ID_PREFIX.length)
					: category.id;

				productsForCategory = products.filter((p) => p.tags?.includes(tagKey));
			} else {
				productsForCategory = realProductsByCategoryId[category.id];
			}

			if (productsForCategory && productsForCategory.length > 0) {
				acc.push({ ...category, products: productsForCategory });
			}

			return acc;
		}, []);
	}, [categories, products]);

	// Use custom hook for intersection observation (performance optimization)
	useIntersectionObserver({
		listRef,
		sectionRefs,
		isClickScrolling,
		onScrollUpdateCategory,
		bannerHeight,
		categoryListHeight,
		categories: productsByCategory,
	});
	const renderFooterLinks = useCallback(
		() =>
			VDPfooter?.links?.map((vdpLinks, index) => (
				<React.Fragment key={vdpLinks.url}>
					<Condition check={index > 0}>|</Condition>
					<a className="CopyrightLink" target="_blank" href={vdpLinks.url}>
						{vdpLinks.name}
					</a>
				</React.Fragment>
			)),
		[VDPfooter]
	);

	return (
		<div ref={listRef} className="ProductListContainer">
			<Condition check={showBanner ?? true}>
				<Condition check={bannerImages.length > 0}>
					<Banner
						images={bannerImages}
						handleCategoryClick={onCategoryClick}
						handleItemClick={onItemClick}
						className={bannerClassName}
					/>
				</Condition>
			</Condition>
			<CategoryList
				categories={categories}
				activeCategoryId={activeCategoryId}
				onCategoryClick={onCategoryClick}
			/>
			{productsByCategory.map((category) => (
				<ProductSection key={category.id} category={category} sectionRefs={sectionRefs} />
			))}
			<Condition check={!!VDPfooter}>
				<div className="ScrollSpacer" />
				<div className="Copyright">
					<div className="CopyrightRow">{VDPfooter?.message}</div>
					<Condition check={!!VDPfooter?.links && isArray(VDPfooter.links)}>
						<div className="CopyrightRow">{renderFooterLinks()}</div>
					</Condition>
				</div>
			</Condition>
			<div className="ScrollSpacer" />
		</div>
	);
};

export default memo(ProductList);
